import { getFeatureConfigSingleton } from './featureConfigSingleton';

export type FeatureConfigPathCallback<FeatureConfig, FeatureConfigReturnType> = (
    features: FeatureConfig,
) => FeatureConfigReturnType;

/**
 * Retrieves a specific feature configuration value.
 *
 * This function provides the ability to configure features for different contexts (e.g., countries)
 * without coupling the features themselves to the country.
 *
 * @template FeatureConfig The type representing the structure of all feature configurations.
 * @template FeatureConfigReturnType The type of the specific configuration value being retrieved.
 *
 * @param featureConfigPathCallback A callback function that navigates through the feature configuration
 * structure and returns the specific configuration value to retrieve.
 *
 * @returns The configuration value of type FeatureConfigReturnType.
 *
 * @example
 * // Basic usage (type is automatically inferred)
 * const maxTransactionAmount = getFeatureConfig(config => config.transactions.maxAmount);
 *
 * @example
 * // Usage with explicit typing
 * const supportedCurrencies = getFeatureConfig<GlobalConfig, string[]>(
 *   config => config.forex.supportedCurrencies
 * );
 *
 * @remarks
 * - The type is automatically injected based on the location where the function is called.
 * - The return type is automatically inferred from the callback.
 * - For features specific to certain applications (e.g., George Business), the type is
 *   automatically scoped to that application's feature set.
 */
export function getFeatureConfig<FeatureConfig, FeatureConfigReturnType>(
    featureConfigPathCallback: FeatureConfigPathCallback<FeatureConfig, FeatureConfigReturnType>,
): FeatureConfigReturnType {
    return featureConfigPathCallback(getFeatureConfigSingleton<FeatureConfig>());
}
